import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Trash2, ShieldCheck, Plus, ArrowRight } from 'lucide-react';
import { normalizeAvisoPhone } from '@conversia/shared-types';
import * as settingsApi from '@/api/settings.api';
import * as routingApi from '@/api/notification-routing.api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { Card, CardBody, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { PageHeader } from '@/components/common/PageHeader';
import { useToast } from '@/hooks/useToast';
import { useAuthStore } from '@/store/auth.store';
import { hasRole } from '@conversia/shared-types';
import { resumenDeRutas } from '@/lib/avisos-destinos';
import { dateTime } from '@/lib/format';

const TIMEZONES = [
  'America/Santo_Domingo',
  'America/New_York',
  'America/Mexico_City',
  'America/Bogota',
  'America/Argentina/Buenos_Aires',
  'Europe/Madrid',
  'UTC',
];

const TZ_POR_DEFECTO = 'America/Santo_Domingo';

function findValue(list: settingsApi.Setting[] | undefined, key: string): string {
  const s = list?.find((x) => x.key === key);
  return s && typeof s.value === 'string' ? s.value : '';
}

function GeneralTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'list'],
    queryFn: settingsApi.listSettings,
  });
  const [name, setName] = useState('');
  const [tz, setTz] = useState(TZ_POR_DEFECTO);

  useEffect(() => {
    if (data) {
      setName(findValue(data, 'general.installationName') || 'Conversia');
      setTz(findValue(data, 'general.timezone') || TZ_POR_DEFECTO);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      await settingsApi.saveSetting('general.installationName', name);
      await settingsApi.saveSetting('general.timezone', tz);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'list'] });
      toast.success('Configuración guardada');
    },
    onError: () => toast.error('No se pudo guardar'),
  });

  if (isLoading) return <Spinner />;

  return (
    <Card>
      <CardHeader>
        <CardTitle>General</CardTitle>
        <CardDescription>
          Identidad y zona horaria de esta instalación. La zona horaria de acá es la que usa todo
          el sistema, incluido el horario del asistente.
        </CardDescription>
      </CardHeader>
      <CardBody className="space-y-4">
        <Input label="Nombre de la instalación" value={name} onChange={(e) => setName(e.target.value)} />
        <Select
          label="Zona horaria"
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          options={TIMEZONES.map((t) => ({ value: t, label: t }))}
        />
        <div>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            Guardar cambios
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function HorarioTab({ onVerGeneral }: { onVerGeneral: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'agent-schedule'],
    queryFn: settingsApi.getAgentSchedule,
  });
  // La zona horaria se elige en UNA sola pestaña (General). Acá solo se muestra
  // cuál es: tener dos selectores para el mismo dato era pedirle al dueño que
  // los mantuviera iguales a mano, y quedarse a medias movía la ventana del
  // asistente sin que nadie lo notara.
  const generales = useQuery({
    queryKey: ['settings', 'list'],
    queryFn: settingsApi.listSettings,
  });
  const [desde, setDesde] = useState(7);
  const [hasta, setHasta] = useState(22);
  const [activo, setActivo] = useState(true);

  useEffect(() => {
    if (data) {
      setDesde(data.responder_desde);
      setHasta(data.responder_hasta);
      setActivo(data.horario_activo);
    }
  }, [data]);

  // Zona horaria efectiva: la de General; si todavía no se guardó ninguna, la
  // que ya traía el horario (no se le cambia el huso por abrir la pestaña).
  const tzGeneral = findValue(generales.data, 'general.timezone');
  const tz = tzGeneral || data?.timezone || TZ_POR_DEFECTO;

  const save = useMutation({
    mutationFn: () =>
      settingsApi.saveAgentSchedule({
        responder_desde: desde,
        responder_hasta: hasta,
        horario_activo: activo,
        timezone: tz,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'agent-schedule'] });
      toast.success('Horario guardado');
    },
    onError: () => toast.error('No se pudo guardar el horario'),
  });

  // Ventana incoherente (hasta <= desde) no se guarda: aviso claro en vez de
  // dejar al asistente mudo sin explicación.
  function guardarHorario() {
    if (activo && hasta <= desde) {
      toast.error('La hora "hasta" debe ser mayor que la hora "desde"');
      return;
    }
    save.mutate();
  }

  if (isLoading) return <Spinner />;

  const hourOpts = (from: number, to: number) =>
    Array.from({ length: to - from + 1 }, (_, i) => from + i).map((h) => ({
      value: String(h),
      label: `${String(h).padStart(2, '0')}:00`,
    }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Horario del agente</CardTitle>
        <CardDescription>
          Ventana en la que el agente responde automáticamente. Fuera de ella, los mensajes quedan
          en la bandeja para un humano.
        </CardDescription>
      </CardHeader>
      <CardBody className="space-y-4">
        <label className="flex cursor-pointer items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">Horario activo</p>
            <p className="text-xs text-slate-400">Si se apaga, el agente responde 24/7.</p>
          </div>
          <input
            type="checkbox"
            data-guia="horario.activo"
            checked={activo}
            onChange={(e) => setActivo(e.target.checked)}
            className="h-5 w-5 accent-brand-600"
          />
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Responder desde"
            value={String(desde)}
            onChange={(e) => setDesde(Number(e.target.value))}
            options={hourOpts(0, 23)}
            disabled={!activo}
          />
          <Select
            label="Responder hasta (excl.)"
            value={String(hasta)}
            onChange={(e) => setHasta(Number(e.target.value))}
            options={hourOpts(1, 24)}
            disabled={!activo}
          />
        </div>
        <p className="flex flex-wrap items-center gap-2 rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
          Usa la zona horaria de General: <b>{tz}</b>
          <button
            type="button"
            onClick={onVerGeneral}
            className="text-xs text-brand-600 hover:underline dark:text-brand-400"
          >
            Cambiarla en General
          </button>
        </p>
        <div>
          <Button data-guia="horario.guardar" onClick={guardarHorario} loading={save.isPending}>
            Guardar horario
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}

function ByokTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: keys, isLoading } = useQuery({
    queryKey: ['settings', 'api-keys'],
    queryFn: () => settingsApi.getApiKeys(),
  });
  const [keyValue, setKeyValue] = useState('');
  const [label, setLabel] = useState('');
  const [borrando, setBorrando] = useState<settingsApi.ApiKeyPublic | null>(null);

  const save = useMutation({
    mutationFn: () => settingsApi.saveApiKey({ key: keyValue, label: label || undefined }),
    onSuccess: () => {
      setKeyValue('');
      setLabel('');
      void qc.invalidateQueries({ queryKey: ['settings', 'api-keys'] });
      toast.success('API key guardada');
    },
    onError: () => toast.error('No se pudo guardar la API key'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => settingsApi.deleteApiKey(id),
    onSuccess: () => {
      setBorrando(null);
      void qc.invalidateQueries({ queryKey: ['settings', 'api-keys'] });
      toast.info('API key eliminada');
    },
    onError: () => toast.error('No se pudo eliminar la API key'),
  });

  const test = useMutation({
    mutationFn: (id: string) => settingsApi.testApiKey(id),
    onSuccess: (res) =>
      res.valid ? toast.success('La API key es válida') : toast.error('La API key no es válida'),
    onError: () => toast.error('No se pudo verificar la API key'),
  });

  const nombreDe = (k: settingsApi.ApiKeyPublic) => k.label || k.provider;

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Clave de IA (Anthropic)</CardTitle>
          <CardDescription>
            Usa tu propia API key de Anthropic (Claude). Se almacena cifrada en el servidor. La
            última que guardes queda como la activa.
          </CardDescription>
        </CardHeader>
        <CardBody className="space-y-4">
          <Input
            data-guia="clave-ia.campo"
            label="API key"
            type="password"
            placeholder="sk-ant-…"
            value={keyValue}
            onChange={(e) => setKeyValue(e.target.value)}
          />
          <Input
            label="Etiqueta (opcional)"
            placeholder="Producción"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <div>
            <Button
              data-guia="clave-ia.guardar"
              onClick={() => save.mutate()}
              loading={save.isPending}
              disabled={keyValue.length < 8}
            >
              <Plus className="h-4 w-4" /> Guardar API key
            </Button>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Keys configuradas</CardTitle>
        </CardHeader>
        <CardBody>
          {isLoading ? (
            <Spinner />
          ) : !keys || keys.length === 0 ? (
            <EmptyState
              icon={KeyRound}
              title="Sin API keys"
              description="Agrega una API key para habilitar los agentes."
            />
          ) : (
            <ul className="divide-y divide-surface-border dark:divide-slate-800">
              {keys.map((k) => (
                <li key={k.id} className="flex flex-wrap items-center gap-3 py-3">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-100 text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
                    <KeyRound className="h-4 w-4" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                      {nombreDe(k)}
                    </p>
                    <p className="truncate font-mono text-xs text-slate-400">{k.masked}</p>
                    {k.createdAt && (
                      <p className="text-xs text-slate-400">Guardada el {dateTime(k.createdAt)}</p>
                    )}
                  </div>
                  {/* Cuál está trabajando: con dos keys cargadas antes no se sabía. */}
                  {k.isActive ? (
                    <Badge variant="success">En uso</Badge>
                  ) : (
                    <Badge variant="default">Guardada</Badge>
                  )}
                  <Badge variant="brand">{k.provider}</Badge>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => test.mutate(k.id)}
                    loading={test.isPending && test.variables === k.id}
                  >
                    <ShieldCheck className="h-4 w-4" /> Probar
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setBorrando(k)}>
                    <Trash2 className="h-4 w-4 text-danger" />
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>

      {/* Borrar una key no se deshace y, si es la que está en uso, el asistente
          deja de responder por IA: se pregunta antes y se dice cuál es. */}
      <Modal
        open={Boolean(borrando)}
        onClose={() => setBorrando(null)}
        title="Borrar la clave de IA"
        footer={
          <>
            <Button variant="outline" onClick={() => setBorrando(null)}>
              Cancelar
            </Button>
            <Button
              variant="danger"
              onClick={() => borrando && remove.mutate(borrando.id)}
              loading={remove.isPending}
            >
              Sí, borrarla
            </Button>
          </>
        }
      >
        <div className="space-y-2 text-sm text-slate-600 dark:text-slate-300">
          <p>
            Se borra <b>{borrando ? nombreDe(borrando) : ''}</b> (
            <span className="font-mono">{borrando?.masked}</span>). No se puede deshacer.
          </p>
          {borrando?.isActive && (
            <p className="font-medium text-danger">
              Es la clave que está en uso: si la borras sin cargar otra, el asistente deja de
              responder por IA.
            </p>
          )}
        </div>
      </Modal>
    </div>
  );
}

/** Preferencias locales de avisos, persistidas de verdad en localStorage. */
const NOTIF_PREFS_KEY = 'conversia.notifs.v1';

function readNotifPrefs(): { email: boolean; sound: boolean } {
  try {
    const raw = localStorage.getItem(NOTIF_PREFS_KEY);
    if (raw) return { email: true, sound: false, ...(JSON.parse(raw) as object) };
  } catch {
    // localStorage bloqueado o JSON corrupto: defaults
  }
  return { email: true, sound: false };
}

/**
 * Fila de una preferencia local. FUERA del render de NotificationsTab: cuando
 * estaba declarada adentro, React la veía como un componente nuevo en cada
 * render y la desmontaba y volvía a montar a cada cambio.
 */
function NotifRow({
  label,
  desc,
  checked,
  onChange,
}: {
  label: string;
  desc: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 py-3">
      <div>
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{label}</p>
        <p className="text-xs text-slate-400">{desc}</p>
      </div>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="h-5 w-5 accent-brand-600"
      />
    </label>
  );
}

function NotificationsTab() {
  const [prefs, setPrefs] = useState(readNotifPrefs);
  const update = (patch: Partial<{ email: boolean; sound: boolean }>) => {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    try {
      localStorage.setItem(NOTIF_PREFS_KEY, JSON.stringify(next));
    } catch {
      // sin localStorage: la preferencia vive solo esta sesión
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle>Notificaciones</CardTitle>
        <CardDescription>Preferencias locales de avisos (se guardan en este navegador).</CardDescription>
      </CardHeader>
      <CardBody>
        <p className="rounded-md bg-slate-50 px-3 py-2 text-xs text-slate-500 dark:bg-slate-800/50 dark:text-slate-400">
          Estos dos ajustes son de <b>este navegador</b>: no cambian nada para el resto del equipo
          ni viajan a tu teléfono. Los avisos que le llegan al dueño por WhatsApp se configuran en
          la pestaña Avisos.
        </p>
        <div className="divide-y divide-surface-border dark:divide-slate-800">
          <NotifRow
            label="Notificaciones por email"
            desc="Recibe un correo cuando una conversación necesita atención humana."
            checked={prefs.email}
            onChange={(v) => update({ email: v })}
          />
          <NotifRow
            label="Sonido de mensajes nuevos"
            desc="Reproduce un sonido al recibir un mensaje en tiempo real."
            checked={prefs.sound}
            onChange={(v) => update({ sound: v })}
          />
        </div>
      </CardBody>
    </Card>
  );
}

/** Avisos al dueño/soporte (operaciones, PASO 4): número que recibe las alertas. */
function OperacionesTab() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'ops-alerts'],
    queryFn: settingsApi.getOwnerAlerts,
  });
  // Solo para CONTAR: cuántos avisos tienen números propios y no dependen de
  // este número. Se elige en Mi negocio → Mensajes; acá no se toca.
  const routing = useQuery({
    queryKey: ['notification-routing'],
    queryFn: routingApi.getRouting,
  });
  const [phone, setPhone] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setPhone(data.owner_phone ?? '');
  }, [data]);

  const save = useMutation({
    mutationFn: (ownerPhone: string) => settingsApi.saveOwnerAlerts({ owner_phone: ownerPhone }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'ops-alerts'] });
      toast.success('Número del dueño guardado');
    },
    onError: () => toast.error('No se pudo guardar'),
  });

  // Código de país obligatorio (el bug del vacío): validar ANTES de guardar.
  // Vacío es válido: significa "sin avisos".
  function guardar() {
    let valor = '';
    if (phone.replace(/\D/g, '') !== '') {
      const normalized = normalizeAvisoPhone(phone);
      if (!normalized.ok) {
        setPhoneError(normalized.error);
        return;
      }
      valor = normalized.phone;
      setPhone(valor);
    }
    setPhoneError(null);
    save.mutate(valor);
  }

  if (isLoading) return <Spinner />;

  const resumen = routing.data ? resumenDeRutas(routing.data.config) : null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Número que recibe los avisos</CardTitle>
        <CardDescription>
          WhatsApp que recibe los avisos internos (SLA vencido, no completado, lead caliente,
          reporte diario…) cuando un aviso no tiene números propios en Mi negocio → Mensajes.
          Formato con código de país (ej. 18295550000); a un número de RD de 10 dígitos el
          sistema le antepone el 1 solo. Vacío = no se envían avisos.
        </CardDescription>
      </CardHeader>
      <CardBody className="space-y-4">
        <Input
          label="Número del dueño / soporte"
          placeholder="18295550000"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={phoneError ?? undefined}
        />
        <div>
          <Button onClick={guardar} loading={save.isPending}>
            Guardar cambios
          </Button>
        </div>
        {/* Resumen de SOLO LECTURA: qué parte de los avisos no depende de este
            número. Editarlos es otra pantalla, y el enlace lleva hasta ella. */}
        <div className="rounded-md bg-slate-50 px-3 py-2.5 text-sm text-slate-600 dark:bg-slate-800/50 dark:text-slate-300">
          {resumen ? (
            <p>
              {resumen.conNumerosPropios === 0 ? (
                <>
                  Ninguno de los {resumen.total} avisos internos tiene números propios: todos caen a
                  este número.
                </>
              ) : (
                <>
                  {resumen.conNumerosPropios} de {resumen.total} avisos internos tienen sus propios
                  números y no pasan por acá.
                </>
              )}
            </p>
          ) : (
            <p>Cargando el resumen de los avisos…</p>
          )}
          <Link
            to="/installation"
            className="mt-1 inline-flex items-center gap-1 text-xs text-brand-600 hover:underline dark:text-brand-400"
          >
            Elegir a quién le llega cada aviso en Mi negocio → Mensajes
            <ArrowRight className="h-3 w-3" />
          </Link>
        </div>
      </CardBody>
    </Card>
  );
}

/** Pestañas válidas de la pantalla, en el orden en que se ven. */
const PESTANAS = ['general', 'horario', 'operaciones', 'byok', 'notifications'] as const;
type Pestana = (typeof PESTANAS)[number];

export default function Settings() {
  // BYOK (API keys) es superficie reservada del OWNER (F2): la API responde
  // 403 a un ADMIN — acá solo se evita mostrarle una pestaña rota.
  const role = useAuthStore((s) => s.user?.role);
  const isOwner = role ? hasRole(role, 'OWNER') : false;

  // La pestaña viaja en la URL (/settings?tab=horario) para poder pasarle a
  // alguien el enlace de la pantalla exacta, no "entra a Configuración y busca".
  const [searchParams, setSearchParams] = useSearchParams();
  const pedida = searchParams.get('tab');
  const valida =
    PESTANAS.includes(pedida as Pestana) && (pedida !== 'byok' || isOwner)
      ? (pedida as Pestana)
      : 'general';

  const irA = (tab: string) => {
    // `replace`: moverse entre pestañas no llena el botón "atrás" del navegador.
    setSearchParams(tab === 'general' ? {} : { tab }, { replace: true });
  };

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Configuración"
        helpKey="settings"
        description="Ajustes generales, clave de IA y notificaciones."
      />
      <Tabs defaultValue="general" value={valida} onValueChange={irA}>
        <TabsList>
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="horario" guia="settings.tab.horario">
            Horario
          </TabsTrigger>
          <TabsTrigger value="operaciones">Avisos</TabsTrigger>
          {isOwner && (
            <TabsTrigger value="byok" guia="settings.tab.clave-ia">
              Clave de IA
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications">Notificaciones</TabsTrigger>
        </TabsList>
        <TabsContent value="general">
          <GeneralTab />
        </TabsContent>
        <TabsContent value="horario">
          <HorarioTab onVerGeneral={() => irA('general')} />
        </TabsContent>
        <TabsContent value="operaciones">
          <OperacionesTab />
        </TabsContent>
        {isOwner && (
          <TabsContent value="byok">
            <ByokTab />
          </TabsContent>
        )}
        <TabsContent value="notifications">
          <NotificationsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
